import {
  ServiceTypeMode,
  ServiceTypeModeSaveDTO,
  useCreateServiceTypeModeMutation,
  useDeleteServiceTypeModeMutation,
  useUpdateServiceTypeModeMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {ResultTypeFrom} from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";
import {EntityMutationCallback} from "./useEntityMutation";

export type UseServiceTypeModeMutationProps = {}

export type UseServiceTypeModeMutationResult = {
  createServiceTypeMode: (serviceTypeMode: ServiceTypeModeSaveDTO, callback: EntityMutationCallback<ServiceTypeMode> | undefined) => void
  updateServiceTypeMode: (serviceTypeMode: ServiceTypeModeSaveDTO, callback: EntityMutationCallback<ServiceTypeMode> | undefined) => void
  deleteServiceTypeMode: (serviceTypeModeId: string, callback: EntityMutationCallback | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
}

export const useServiceTypeModeMutation = (props: UseServiceTypeModeMutationProps = {}): UseServiceTypeModeMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_createServiceTypeMode, {isLoading: isCreating}] = useCreateServiceTypeModeMutation()
  const [_updateServiceTypeMode, {isLoading: isUpdating}] = useUpdateServiceTypeModeMutation()
  const [_deleteServiceTypeMode, {isLoading: isDeleting}] = useDeleteServiceTypeModeMutation()

  useEffect(() => {
    _setCreating(isCreating)
  }, [isCreating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating)
  }, [isUpdating, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "useServiceTypeModeUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.servicetypemode", "Service Type Mode"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useServiceTypeModeUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "useServiceTypeModeUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.servicetypemode", "Service Type Mode"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useServiceTypeModeUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useServiceTypeModeUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.servicetypemode", "Service Type Mode"),
        message: "",
        loading: true,
        autoClose: 5000,
      });
    } else {
      notifications.hide("useServiceTypeModeUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleCreateResult = useCallback((promise: Promise<ServiceTypeMode>, callback: EntityMutationCallback<ServiceTypeMode> | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.servicetypemode", "Service Type Mode") + " " + t("common.status.created", "Created"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(payload)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, callback: EntityMutationCallback<ServiceTypeMode> | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.servicetypemode", "Service Type Mode") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(undefined)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, callback: EntityMutationCallback | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.servicetypemode", "Service Type Mode") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (callback?.onSuccess) {
          callback.onSuccess(undefined)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteConfirm = useCallback((serviceTypeModeId: string, callback: EntityMutationCallback<ServiceTypeMode> | undefined) => {
    const promise = _deleteServiceTypeMode({id: serviceTypeModeId}).unwrap()
    handleDeleteResult(promise, callback)
  }, [_deleteServiceTypeMode, handleDeleteResult])

  const createServiceTypeMode = useCallback((serviceTypeMode: ServiceTypeModeSaveDTO, callback: EntityMutationCallback<ServiceTypeMode> | undefined) => {
    const promise = _createServiceTypeMode(serviceTypeMode).unwrap()
    handleCreateResult(promise, callback)
  }, [_createServiceTypeMode, handleCreateResult])

  const updateServiceTypeMode = useCallback((serviceTypeMode: ServiceTypeModeSaveDTO, callback: EntityMutationCallback | undefined) => {
    const promise = _updateServiceTypeMode(serviceTypeMode).unwrap()
    handleUpdateResult(promise, callback)
  }, [_updateServiceTypeMode, handleUpdateResult])

  const deleteServiceTypeMode = useCallback((serviceTypeModeId: string, callback: EntityMutationCallback | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.servicetypemode", "Service Type Mode"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.delete.servicetypemode.confirm", "Are you sure you want to delete this service type mode?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(serviceTypeModeId, callback)
      },
    })
  }, [handleDeleteConfirm, t])

  return {
    createServiceTypeMode: createServiceTypeMode,
    updateServiceTypeMode: updateServiceTypeMode,
    deleteServiceTypeMode: deleteServiceTypeMode,

    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
  }
}
